import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Star, Settings, Gift, Plus, Pencil, CheckCircle, XCircle, Clock, ScanLine, Users, Send, Camera } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useEffect as useEffectHook, useRef } from 'react';
import { ResultOverlay } from '../../components/ResultOverlay';

export default function AdminLoyalty() {
  const [tab, setTab] = useState('settings');
  const [settings, setSettings] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rewardOpen, setRewardOpen] = useState(false);
  const [rewardForm, setRewardForm] = useState({ name: '', description: '', cost_points: '', type: 'cocktail' });
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [massOpen, setMassOpen] = useState(false);
  const [massForm, setMassForm] = useState({ amount: '100', description: 'Bonus événement' });
  const [events, setEvents] = useState([]);
  const [overlay, setOverlay] = useState({ show: false, type: 'success', title: '', subtitle: '' });

  const load = () => Promise.all([
    api.get('/admin/loyalty/settings'),
    api.get('/admin/rewards'),
    api.get('/admin/reward-orders'),
    api.get('/admin/events'),
  ]).then(([s, r, o, e]) => { setSettings(s.data); setRewards(r.data); setOrders(o.data); setEvents(e.data); })
    .catch(() => {}).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    try {
      await api.put('/admin/loyalty/settings', { rules: settings });
      toast.success('Paramètres sauvegardés');
    } catch { toast.error('Erreur'); }
  };

  const updateRule = (ruleId, field, value) => {
    setSettings(prev => prev.map(r => r.rule_id === ruleId ? { ...r, [field]: value } : r));
  };

  const saveReward = async () => {
    try {
      await api.post('/admin/rewards', { ...rewardForm, cost_points: parseInt(rewardForm.cost_points) });
      toast.success('Récompense créée');
      setRewardOpen(false);
      load();
    } catch { toast.error('Erreur'); }
  };

  const updateOrder = async (oid, status) => {
    try {
      await api.put(`/admin/reward-orders/${oid}`, { status });
      if (status === 'confirmed') {
        setOverlay({ show: true, type: 'success', title: 'Commande confirmée !', subtitle: 'Le client a été notifié' });
      } else {
        setOverlay({ show: true, type: 'error', title: 'Commande refusée', subtitle: 'Les points ont été remboursés au client' });
      }
      load();
    } catch { toast.error('Erreur'); }
  };

  const toggleReward = async (rid, active) => {
    try {
      await api.put(`/admin/rewards/${rid}`, { active: !active });
      toast.success('Mis à jour');
      load();
    } catch { toast.error('Erreur'); }
  };

  const handleQrScan = async (qrUuid) => {
    try {
      const { data } = await api.get(`/admin/qr-lookup/${qrUuid}`);
      setScanResult(data);
      setScanning(false);
      toast.success(`Client trouvé : ${data.user.name}`);
    } catch { toast.error('QR code inconnu'); setScanning(false); }
  };

  const handleMassPoints = async () => {
    try {
      const { data } = await api.post('/admin/loyalty/mass-points', massForm);
      toast.success(`${data.count} clients crédités de ${massForm.amount} pts`);
      setMassOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const ruleLabels = {
    pts_per_euro: "Points par euro dépensé",
    pts_rdv: "Points par RDV honoré",
    pts_event: "Points par présence événement",
    pts_welcome: "Points de bienvenue inscription",
  };

  if (loading) return <div className="flex justify-center py-20"><div className="spin-gold w-8 h-8" /></div>;

  return (
    <div className="fade-in" data-testid="admin-loyalty-page">
      <ResultOverlay {...overlay} onClose={() => setOverlay(p => ({ ...p, show: false }))} />
      <h1 className="text-2xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Outfit' }}>{"Fidélité & Points"}</h1>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#121212] border border-white/5 h-auto p-1 mb-6">
          <TabsTrigger value="settings" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black rounded-full text-xs px-4 gap-1.5">
            <Settings className="w-3.5 h-3.5" /> {"Paramètres"}
          </TabsTrigger>
          <TabsTrigger value="rewards" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black rounded-full text-xs px-4 gap-1.5">
            <Gift className="w-3.5 h-3.5" /> {"Récompenses"}
          </TabsTrigger>
          <TabsTrigger value="orders" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black rounded-full text-xs px-4 gap-1.5">
            <Star className="w-3.5 h-3.5" /> Commandes ({orders.filter(o => o.status === 'pending').length})
          </TabsTrigger>
          <TabsTrigger value="scanner" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black rounded-full text-xs px-4 gap-1.5">
            <ScanLine className="w-3.5 h-3.5" /> Scanner QR
          </TabsTrigger>
          <TabsTrigger value="validate" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black rounded-full text-xs px-4 gap-1.5">
            <Gift className="w-3.5 h-3.5" /> Valider bon
          </TabsTrigger>
          <TabsTrigger value="mass" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black rounded-full text-xs px-4 gap-1.5">
            <Users className="w-3.5 h-3.5" /> Points en masse
          </TabsTrigger>
        </TabsList>

        {/* Settings */}
        <TabsContent value="settings">
          <div className="bg-[#121212] border border-white/5 rounded-xl p-6 space-y-5">
            <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>{"Règles d'attribution"}</h2>
            {settings.map(rule => (
              <div key={rule.rule_id} className="flex items-center gap-4 py-3 border-b border-white/5 last:border-0" data-testid={`rule-${rule.rule_id}`}>
                <Switch checked={rule.active} onCheckedChange={(v) => updateRule(rule.rule_id, 'active', v)} />
                <div className="flex-1">
                  <div className="text-sm font-medium">{ruleLabels[rule.rule_id] || rule.rule_id}</div>
                </div>
                <Input type="number" value={rule.value} onChange={e => updateRule(rule.rule_id, 'value', parseInt(e.target.value) || 0)}
                  className="w-24 bg-[#0A0A0A] border-white/10 rounded-lg text-right text-sm" />
                <span className="text-xs text-[#A1A1AA] w-8">pts</span>
              </div>
            ))}
            <Button onClick={saveSettings} data-testid="save-loyalty-settings"
              className="bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full px-6">Sauvegarder</Button>
          </div>
        </TabsContent>

        {/* Rewards */}
        <TabsContent value="rewards">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>{"Récompenses"}</h2>
            <Button onClick={() => { setRewardForm({ name: '', description: '', cost_points: '', type: 'cocktail' }); setRewardOpen(true); }}
              className="bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full px-4" data-testid="create-reward-btn">
              <Plus className="w-4 h-4 mr-1" /> Ajouter
            </Button>
          </div>
          <div className="space-y-3">
            {rewards.map(r => (
              <div key={r.reward_id} className="bg-[#121212] border border-white/5 rounded-xl p-4 flex items-center gap-4" data-testid={`admin-reward-${r.reward_id}`}>
                <Gift className="w-5 h-5 text-[#D4AF37]" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">{r.name}</span>
                    {!r.active && <Badge className="bg-red-500/15 text-red-400 text-[10px]">{"Désactivé"}</Badge>}
                  </div>
                  <div className="text-xs text-[#A1A1AA]">{r.description}</div>
                </div>
                <span className="text-sm font-bold text-[#D4AF37]" style={{ fontFamily: 'JetBrains Mono' }}>{r.cost_points} pts</span>
                <Button size="sm" variant="ghost" onClick={() => toggleReward(r.reward_id, r.active)}
                  className="text-[#A1A1AA] text-xs">{r.active ? 'Désactiver' : 'Activer'}</Button>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Orders */}
        <TabsContent value="orders">
          <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Outfit' }}>Commandes de récompenses</h2>
          {orders.length === 0 ? (
            <p className="text-sm text-[#A1A1AA] text-center py-8">Aucune commande</p>
          ) : (
            <div className="space-y-3">
              {orders.map(o => (
                <div key={o.order_id} className="bg-[#121212] border border-white/5 rounded-xl p-4 flex items-center gap-4" data-testid={`order-${o.order_id}`}>
                  <div className={`p-1.5 rounded-md ${o.status === 'pending' ? 'bg-yellow-500/10' : o.status === 'confirmed' ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                    {o.status === 'pending' ? <Clock className="w-4 h-4 text-yellow-400" /> : o.status === 'confirmed' ? <CheckCircle className="w-4 h-4 text-green-400" /> : <XCircle className="w-4 h-4 text-red-400" />}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{o.reward_name}</div>
                    <div className="text-[10px] text-[#A1A1AA]">{o.cost_points} pts - {new Date(o.created_at).toLocaleDateString('fr-FR')}</div>
                  </div>
                  {o.status === 'pending' && (
                    <div className="flex gap-1">
                      <Button size="sm" onClick={() => updateOrder(o.order_id, 'confirmed')}
                        className="bg-[#10B981] text-white hover:bg-[#10B981]/80 rounded-full text-xs h-7 px-3" data-testid={`confirm-order-${o.order_id}`}>Confirmer</Button>
                      <Button size="sm" onClick={() => updateOrder(o.order_id, 'refused')}
                        className="bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-full text-xs h-7 px-3" data-testid={`refuse-order-${o.order_id}`}>Refuser</Button>
                    </div>
                  )}
                  {o.status !== 'pending' && <Badge className={statusColors[o.status] || 'bg-white/10'}>{statusLabels[o.status] || o.status}</Badge>}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* QR Scanner Tab Content - rendered outside Tabs for simplicity */}
      {tab === 'scanner' && (
        <div className="mt-6" data-testid="qr-scanner-section">
          {!scanResult ? (
            <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
              <div className="text-center mb-6">
                <ScanLine className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>Scanner un QR Code</h3>
                <p className="text-sm text-[#A1A1AA]">{"Utilisez la caméra ou entrez l'UUID manuellement"}</p>
              </div>
              {/* Camera Scanner */}
              {scanning ? (
                <div className="mb-6">
                  <div id="qr-reader" className="max-w-sm mx-auto rounded-xl overflow-hidden" style={{ border: '2px solid #D4AF37' }}></div>
                  <QrCameraScanner onScan={handleQrScan} onStop={() => setScanning(false)} />
                  <div className="text-center mt-3">
                    <Button onClick={() => setScanning(false)} variant="outline" className="border-white/10 rounded-full text-xs">{"Arrêter la caméra"}</Button>
                  </div>
                </div>
              ) : (
                <div className="text-center mb-6">
                  <Button onClick={() => setScanning(true)} className="bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full px-6" data-testid="start-camera-btn">
                    <Camera className="w-4 h-4 mr-2" /> {"Activer la caméra"}
                  </Button>
                </div>
              )}
              {/* Manual input */}
              <div className="max-w-sm mx-auto">
                <p className="text-xs text-[#A1A1AA] text-center mb-2">{"Ou entrez l'UUID manuellement :"}</p>
                <Input placeholder="UUID du QR code (ex: a1b2c3d4-...)" data-testid="qr-manual-input"
                  className="bg-[#0A0A0A] border-white/10 rounded-xl"
                  onKeyDown={e => { if (e.key === 'Enter') handleQrScan(e.target.value.trim()); }} />
              </div>
            </div>
          ) : (
            <div className="bg-[#121212] border border-white/5 rounded-xl p-6" data-testid="qr-scan-result">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>{scanResult.user.name}</h3>
                  <p className="text-xs text-[#A1A1AA]">{scanResult.user.email} {scanResult.user.phone && `| ${scanResult.user.phone}`}</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-[#D4AF37]" style={{ fontFamily: 'JetBrains Mono' }}>{scanResult.user.points_solde || 0}</div>
                  <div className="text-[10px] text-[#A1A1AA]">points</div>
                </div>
              </div>
              {scanResult.recent_transactions.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-[#A1A1AA] mb-2">{"Dernières transactions"}</h4>
                  {scanResult.recent_transactions.slice(0, 5).map(tx => (
                    <div key={tx.tx_id} className="flex justify-between text-xs py-1.5 border-b border-white/5">
                      <span>{tx.description}</span>
                      <span className={tx.amount > 0 ? 'text-[#10B981]' : 'text-[#EF4444]'} style={{ fontFamily: 'JetBrains Mono' }}>{tx.amount > 0 ? '+' : ''}{tx.amount}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={() => setScanResult(null)} variant="outline" className="border-white/10 rounded-full text-xs">Nouveau scan</Button>
                <Button onClick={() => { setTab('settings'); }} variant="ghost" className="text-[#D4AF37] text-xs">{"Gérer les points"}</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'validate' && (
        <div className="mt-6" data-testid="validate-bon-section">
          <ValidateBon />
        </div>
      )}

      {tab === 'mass' && (
        <div className="mt-6" data-testid="mass-points-section">
          <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
            <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Outfit' }}>Attribution en masse</h3>
            <div className="space-y-4 max-w-md">
              <div><Label className="text-xs text-[#A1A1AA]">Points à attribuer</Label>
                <Input type="number" value={massForm.amount} onChange={e => setMassForm(p => ({ ...p, amount: e.target.value }))}
                  data-testid="mass-amount" className="bg-[#0A0A0A] border-white/10 rounded-xl mt-1" /></div>
              <div><Label className="text-xs text-[#A1A1AA]">Description</Label>
                <Input value={massForm.description} onChange={e => setMassForm(p => ({ ...p, description: e.target.value }))}
                  data-testid="mass-description" className="bg-[#0A0A0A] border-white/10 rounded-xl mt-1" /></div>
              <div><Label className="text-xs text-[#A1A1AA]">{"Filtrer par événement (optionnel)"}</Label>
                <Select value={massForm.event_id || ''} onValueChange={v => setMassForm(p => ({ ...p, event_id: v }))}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10 rounded-xl mt-1" data-testid="mass-event"><SelectValue placeholder="Tous les clients" /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    <SelectItem value="">Tous les clients</SelectItem>
                    {events.map(e => <SelectItem key={e.event_id} value={e.event_id}>{e.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleMassPoints} data-testid="mass-submit" className="bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full px-6">
                <Send className="w-4 h-4 mr-2" /> Attribuer les points
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create Reward Dialog */}
      <Dialog open={rewardOpen} onOpenChange={setRewardOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-sm">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }}>{"Nouvelle récompense"}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div><Label className="text-xs text-[#A1A1AA]">Nom *</Label>
              <Input value={rewardForm.name} onChange={e => setRewardForm(p => ({ ...p, name: e.target.value }))}
                data-testid="reward-form-name" className="bg-[#121212] border-white/10 rounded-xl mt-1" /></div>
            <div><Label className="text-xs text-[#A1A1AA]">Description</Label>
              <Input value={rewardForm.description} onChange={e => setRewardForm(p => ({ ...p, description: e.target.value }))}
                data-testid="reward-form-desc" className="bg-[#121212] border-white/10 rounded-xl mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-[#A1A1AA]">{"Coût (points) *"}</Label>
                <Input type="number" value={rewardForm.cost_points} onChange={e => setRewardForm(p => ({ ...p, cost_points: e.target.value }))}
                  data-testid="reward-form-cost" className="bg-[#121212] border-white/10 rounded-xl mt-1" /></div>
              <div><Label className="text-xs text-[#A1A1AA]">Type</Label>
                <Select value={rewardForm.type} onValueChange={v => setRewardForm(p => ({ ...p, type: v }))}>
                  <SelectTrigger className="bg-[#121212] border-white/10 rounded-xl mt-1" data-testid="reward-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    <SelectItem value="cocktail">Cocktail offert</SelectItem>
                    <SelectItem value="reduction">{"Réduction %"}</SelectItem>
                    <SelectItem value="prestation">Prestation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={saveReward} data-testid="reward-form-save" className="w-full bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full">{"Créer"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const statusColors = { pending: 'bg-yellow-500/15 text-yellow-400', confirmed: 'bg-green-500/15 text-green-400', refused: 'bg-red-500/15 text-red-400' };
const statusLabels = { pending: 'En attente', confirmed: 'Confirmé', refused: 'Refusé' };

function QrCameraScanner({ onScan, onStop }) {
  const scannerRef = useRef(null);
  useEffectHook(() => {
    let scanner = null;
    const init = async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        scanner = new Html5Qrcode("qr-reader");
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decodedText) => {
            scanner.stop().catch(() => {});
            onScan(decodedText);
          },
          () => {}
        );
      } catch (err) {
        console.error("Camera error:", err);
      }
    };
    init();
    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
      }
    };
  }, [onScan]);
  return null;
}

function ValidateBon() {
  const [result, setResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);

  const handleValidate = async (qrToken) => {
    try {
      const { data } = await api.post('/admin/reward-qr/validate', { qr_token: qrToken });
      setResult(data);
      setScanning(false);
      toast.success(`Bon validé : ${data.reward_name} pour ${data.user_name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'QR code invalide');
      setScanning(false);
    }
  };

  const startCam = async () => {
    setScanning(true);
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const s = new Html5Qrcode("bon-qr-reader");
      scannerRef.current = s;
      await s.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 250, height: 250 } },
        (text) => { s.stop().catch(() => {}); handleValidate(text); }, () => {});
    } catch { setScanning(false); }
  };

  return (
    <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
      <div className="text-center mb-6">
        <Gift className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>Valider un bon de récompense</h3>
        <p className="text-sm text-[#A1A1AA]">{"Scannez le QR code du bon pour le valider (usage unique)"}</p>
      </div>
      {result ? (
        <div className="text-center bg-[#10B981]/10 border border-[#10B981]/20 rounded-xl p-6">
          <CheckCircle className="w-12 h-12 text-[#10B981] mx-auto mb-3" />
          <h4 className="font-semibold text-lg text-[#10B981]" style={{ fontFamily: 'Outfit' }}>Bon validé !</h4>
          <p className="text-sm mt-2">{result.reward_name}</p>
          <p className="text-xs text-[#A1A1AA]">Client : {result.user_name} | {result.cost_points} pts</p>
          <Button onClick={() => setResult(null)} variant="outline" className="mt-4 border-white/10 rounded-full text-xs">Nouveau scan</Button>
        </div>
      ) : (
        <>
          {scanning ? (
            <div className="max-w-sm mx-auto">
              <div id="bon-qr-reader" className="rounded-xl overflow-hidden mb-3" style={{ border: '2px solid #D4AF37' }}></div>
              <div className="text-center"><Button onClick={() => { if(scannerRef.current) scannerRef.current.stop().catch(()=>{}); setScanning(false); }} variant="outline" className="border-white/10 rounded-full text-xs">{"Arrêter"}</Button></div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Button onClick={startCam} className="bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full px-6" data-testid="bon-start-camera">
                <Camera className="w-4 h-4 mr-2" /> {"Activer la caméra"}
              </Button>
              <div className="flex gap-2 items-center">
                <span className="text-xs text-[#A1A1AA]">ou UUID :</span>
                <Input placeholder="QR token..." className="w-48 bg-[#0A0A0A] border-white/10 rounded-xl text-xs h-9"
                  onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) handleValidate(e.target.value.trim()); }} />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
